// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OverflowTypography } from '@/components/overflow-typography';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { PluralizeOptions, pluralize } from '@/utilities/pluralize';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Link, LinkProps } from '@tanstack/react-router';
import { AxisLeft, TickRendererProps } from '@visx/axis';
import { localPoint } from '@visx/event';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { BarStackHorizontal } from '@visx/shape';
import { BarStack } from '@visx/shape/lib/types';
import { Text, getStringWidth } from '@visx/text';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { MouseEventHandler, useRef } from 'react';

/**
 * Determines if a color is light or dark and returns appropriate text color
 * @param hexColor - The hex color to analyze
 * @returns 'white' for dark backgrounds, 'black' for light backgrounds
 */
function getContrastTextColor(hexColor: string): string {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate luminance using relative luminance formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return white for dark backgrounds, black for light backgrounds
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

const MIN_LABEL_WIDTH = 40; // Minimum width in pixels to show a label
const BAR_HEIGHT = 30;
const BAR_MARGIN = 24;
const TICK_LENGTH = 8;
const LABEL_WIDTH = 120;
const LABEL_OFFSET = TICK_LENGTH + 8;
const COUNT_OFFSET = 24;
const TOOLTIP_OFFSET_X = 8;

export type BarDatum<Key extends string> = {
  totalCount: number;
} & Record<Key, number>;

interface TooltipData<Key> {
  key: Key;
  label: string;
  count: number;
}

/**
 * Renders a tick for the y-axis of the bar chart.
 *
 * @param props - The props for the tick element.
 * @param hideDatumTextLabels - Whether to hide the datum text labels.
 */
function renderTick(hideDatumTextLabels: boolean) {
  return (props: TickRendererProps) => {
    if (hideDatumTextLabels) {
      return null;
    }

    const x = -LABEL_WIDTH;
    const y = props.y - BAR_HEIGHT / 2;
    const width = LABEL_WIDTH - TICK_LENGTH;

    return (
      <foreignObject x={x} y={y} width={width} height={BAR_HEIGHT}>
        <Box display="flex" alignItems="center" height="100%">
          <OverflowTypography
            maxWidth={LABEL_WIDTH}
            color="text.secondary"
            variant="body2"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            {props.formattedValue}
          </OverflowTypography>
        </Box>
      </foreignObject>
    );
  };
}

export interface Props<Datum, Key> {
  data: Datum[];
  keys: Key[];
  colors: string[];
  emptyState: string;
  getHref?: (datum: Datum, key: Key) => Pick<LinkProps, 'to' | 'search'>;
  getLabelForKey: (key: Key) => string;
  getLabelForDatum: (datum: Datum) => string;
  hideDatumTextLabels?: boolean;
  hideTotalValueLabels?: boolean;
  showSegmentValueLabels?: boolean;
  pluralizeTotalOptions?: PluralizeOptions;
  pluralizeTooltipOptions?: PluralizeOptions;
  pluralizeLabelOptions?: PluralizeOptions;
  pluralizeTotalValue?: boolean;
  minimumHeight?: number | string;
  maximumHeight?: number | string;
  padding?: number;
  hideAxis?: boolean;
}

export function BarChart<Key extends string, Datum extends BarDatum<Key>>(
  props: Props<Datum, Key>
) {
  // SVG content minimum height (always numeric for Math.max calculation)
  const SVG_MIN_HEIGHT =
    typeof props.minimumHeight === 'number' ? props.minimumHeight : 200;
  // Box container constraints (can be string like '40rem' or number)
  const containerMinHeight = props.minimumHeight ?? 200;
  const containerMaxHeight = props.maximumHeight ?? 240;

  // Calculate AXIS_LEFT based on whether labels are hidden
  const AXIS_LEFT = props.hideDatumTextLabels
    ? TICK_LENGTH
    : LABEL_OFFSET + LABEL_WIDTH;

  const theme = useTheme();
  const ref = useRef<HTMLDivElement | null>(null);
  const resizeObserver = useResizeObserver(ref);

  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipOpen,
    tooltipTop = 0,
    tooltipLeft = 0,
  } = useTooltip<TooltipData<Key>>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const tooltipStyle = {
    ...defaultStyles,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  };

  const count = props.data.reduce(
    (count, { totalCount }) => Math.max(count, totalCount),
    0
  );

  const height = Math.max(
    SVG_MIN_HEIGHT,
    props.data.length * BAR_HEIGHT + (props.data.length - 1) * BAR_MARGIN
  );

  const countWidth = props.hideTotalValueLabels
    ? 0
    : TICK_LENGTH +
      COUNT_OFFSET +
      (getStringWidth(count.toLocaleString()) ?? 0);

  const rowScale = scaleBand({
    domain: props.data.map(props.getLabelForDatum),
    range: [0, height],
  });

  const countScale = scaleLinear({
    domain: [0, count],
    range: [AXIS_LEFT, resizeObserver.width - countWidth],
  });

  const colorScale = scaleOrdinal({
    domain: props.keys,
    range: props.colors,
  });

  const getLabelX = (datum: Datum) => {
    return countScale(datum.totalCount) + TICK_LENGTH;
  };

  const getLabelY = (datum: Datum) => {
    const y = rowScale(props.getLabelForDatum(datum));

    if (y === undefined) {
      return 0;
    }

    return y + rowScale.bandwidth() / 2;
  };

  const renderBarStack = (stack: BarStack<Datum, Key>, index: number) => {
    return stack.bars.map((bar) => {
      const key = `${stack.index}-${index}`;
      const y = bar.y + (rowScale.bandwidth() - BAR_HEIGHT) / 2;

      const onMouseMove: MouseEventHandler<SVGElement> = (event) => {
        const point = localPoint(event);

        if (point === null) {
          return;
        }

        showTooltip({
          tooltipTop: point.y,
          tooltipLeft: point.x + TOOLTIP_OFFSET_X,
          tooltipData: {
            key: stack.key,
            count: bar.bar.data[stack.key],
            label: props.getLabelForDatum(bar.bar.data),
          },
        });
      };

      const onMouseLeave = () => {
        hideTooltip();
      };

      // Only show label if the segment is wide enough to display it
      const segmentLabel = props.showSegmentValueLabels &&
        bar.width >= MIN_LABEL_WIDTH && (
          <Text
            x={bar.x + bar.width / 2}
            y={y + BAR_HEIGHT / 2}
            fill={getContrastTextColor(bar.color)}
            style={theme.typography.body2}
            verticalAnchor="middle"
            textAnchor="middle"
          >
            {props.pluralizeLabelOptions
              ? pluralize(bar.bar.data[stack.key], props.pluralizeLabelOptions)
              : bar.bar.data[stack.key].toLocaleString()}
          </Text>
        );

      if (props.getHref === undefined) {
        return (
          <g key={key}>
            <rect
              x={bar.x}
              y={y}
              fill={bar.color}
              width={bar.width}
              height={BAR_HEIGHT}
              onMouseMove={onMouseMove}
              onMouseLeave={onMouseLeave}
            />
            {segmentLabel}
          </g>
        );
      }

      const link = props.getHref(bar.bar.data, stack.key);

      return (
        <g key={key}>
          <Link to={link.to} search={link.search}>
            <rect
              x={bar.x}
              y={y}
              fill={bar.color}
              width={bar.width}
              height={BAR_HEIGHT}
              onMouseMove={onMouseMove}
              onMouseLeave={onMouseLeave}
            />
          </Link>
          {segmentLabel}
        </g>
      );
    });
  };

  return (
    <Box
      ref={ref}
      flex={1}
      padding={props.padding ?? 2}
      display="flex"
      overflow="auto"
      position="relative"
      maxHeight={containerMaxHeight}
      minHeight={containerMinHeight}
    >
      {props.data.length === 0 ? (
        <Typography color="text.secondary" alignSelf="center" marginX="auto">
          {props.emptyState}
        </Typography>
      ) : (
        <svg
          ref={containerRef}
          width="100%"
          height={height}
          style={{ display: 'block' }}
        >
          {!props.hideAxis && (
            <AxisLeft
              hideTicks
              numTicks={props.data.length}
              left={AXIS_LEFT}
              scale={rowScale}
              stroke={theme.palette.divider}
              tickLength={TICK_LENGTH}
              tickStroke={theme.palette.text.primary}
              tickComponent={renderTick(props.hideDatumTextLabels ?? false)}
            />
          )}
          {tooltipOpen && (
            <TooltipInPortal
              top={tooltipTop}
              left={tooltipLeft}
              style={tooltipStyle}
            >
              {tooltipData && (
                <Stack>
                  <Typography variant="subtitle2">
                    {tooltipData.label}{' '}
                    <Typography variant="body2" component="span">
                      ({props.getLabelForKey(tooltipData.key)})
                    </Typography>
                  </Typography>
                  <Typography variant="body2">
                    {props.pluralizeTooltipOptions
                      ? pluralize(
                          tooltipData.count,
                          props.pluralizeTooltipOptions
                        )
                      : tooltipData.count.toLocaleString()}
                  </Typography>
                </Stack>
              )}
            </TooltipInPortal>
          )}
          {!props.hideTotalValueLabels &&
            props.data.map((datum) => (
              <Text
                key={props.getLabelForDatum(datum)}
                x={getLabelX(datum)}
                y={getLabelY(datum)}
                fill={theme.palette.text.primary}
                style={{ ...theme.typography.body2, fontWeight: 'bold' }}
                verticalAnchor="middle"
              >
                {props.pluralizeTotalValue && props.pluralizeTotalOptions
                  ? pluralize(datum.totalCount, props.pluralizeTotalOptions)
                  : datum.totalCount.toLocaleString()}
              </Text>
            ))}
          <BarStackHorizontal
            y={props.getLabelForDatum}
            keys={props.keys}
            data={props.data}
            color={colorScale}
            yScale={rowScale}
            xScale={countScale}
            height={BAR_HEIGHT}
          >
            {(barStacks) => barStacks.map(renderBarStack)}
          </BarStackHorizontal>
        </svg>
      )}
    </Box>
  );
}
